/**
 * API Health Check Utility
 * 
 * Simple utility to check if the API endpoints are responsive before
 * making actual prediction requests.
 */

import axios from 'axios';
import { apiLogger } from './apiLogger';

/**
 * Check if the API server is available and responsive
 * @param baseUrl - The base URL of the API server
 * @returns Object containing status and response time
 */
export const checkApiHealth = async (baseUrl: string): Promise<{
  isAvailable: boolean;
  responseTime?: number;
  error?: any;
}> => {
  try {
    const startTime = performance.now();
    
    // Try a simple GET request to check if the API is responding.
    // Any HTTP response (even 4xx/5xx) proves the server is reachable —
    // we only treat network failures as unavailable.
    const response = await axios.get(baseUrl, {
      timeout: 5000,
      validateStatus: () => true
    });

    const endTime = performance.now();
    const responseTime = endTime - startTime;

    apiLogger.debug('API health check', {
      baseUrl,
      status: response.status,
      responseTime: `${responseTime.toFixed(2)}ms`
    });

    const isAvailable = true;
    
    return {
      isAvailable,
      responseTime
    };
  } catch (error) {
    apiLogger.error('API health check failed', error);
    return {
      isAvailable: false,
      error
    };
  }
};

/**
 * Check the API status before making a request
 * @param url - The API endpoint URL
 * @returns A message about the API status
 */
export const getApiStatusMessage = async (url: string): Promise<string> => {
  try {
    // Extract base URL from the full endpoint
    const baseUrl = new URL(url).origin;
    const health = await checkApiHealth(baseUrl);
    
    if (health.isAvailable) {
      return `API is available (response time: ${health.responseTime?.toFixed(0)}ms)`;
    } else if (health.error) {
      if (axios.isAxiosError(health.error)) {
        return `API connection issue: ${health.error.message}`;
      } else {
        return `API check failed: ${health.error.message || 'Unknown error'}`;
      }
    } else {
      return 'API returned an error status';
    }
  } catch (error) {
    return 'Could not check API status';
  }
};

export default {
  checkApiHealth,
  getApiStatusMessage
};
